import i18next from 'i18next'
import { Map } from 'immutable'
import React from 'react'
import { DataLayer } from '../../../../utils/datalayer'
import { Image } from '../../../../utils/image'
import { ImageSize, pageheaderHeroSrc, pageheaderHeroSrcSet } from '../../../../utils/imageSizes'
import { isUrlExternal, Link } from '../../../../utils/link'
import { DecorativeIcon } from '../../../../utils/spaarneIcons'
import { HeroPageHeader } from '../../../sharedLayout/pageheader'

type HomePageHeaderProps = {
  title: string
  intro: string
  // vimeoSrc: AsyncState<string>
}

// VIMEO VIDEO SETTINGS
// Make sure that in Vimeo the privacy settings of the video are 'Public'. To be able to view the video locally, make sure that 'Where can the video be embedded?' is set to 'Anywhere'.
// When replacing the video URL:
// - Choose responsive sizing
// - Make sure that the parameters below stay intact (so only change the video ID e.g. '/332153906?h=291a6f7936')

type TopTask = {
  label: string
  url: string
  icon?: string
  extraLabel?: string
}

const HomepageTopTasksLayout = (props: { topTasks: TopTask[] }) => {
  return  (
    <div className="pageheader__toptasks">
      {props.topTasks.map((task, index) => {
        return (
        <Link
          url={task.url}
          className={`pageheader__toptask pageheader__toptasks--link-${index}`}
          additionalOnClick={(_) => {
            DataLayer.topTasks(task.label)
          }}
        >
          <div className={`pageheader__toptask--inner item${index == 0 ? '-highlighted' : '-regular'}`}>
            {task.icon && <div className="pageheader__toptask--icon">
              <svg className="" aria-hidden="true" width="26px" height="26px">
                <use xlinkHref={`#icon--${task.icon}`}></use>
              </svg>
            </div>}
            <div className={`pageheader__toptask--label ${isUrlExternal(task.url) ? ' link--external' : ''}`}>
              <span>{task.label}</span>
              {isUrlExternal(task.url) && <DecorativeIcon iconId="icon--external" width="20px" height="20px" className="icon--external-link" />}
            </div>
            <div className="pageheader__toptask--extra-label">{task.extraLabel}</div>
          </div>
        </Link>
      )
      })}
    </div>
  )
}

export const HomePageHeader = (props: HomePageHeaderProps) => {
  const topTasks: {
    nl: TopTask[]
    en: TopTask[]
  } = {
    nl: [
      {
        label: 'Alles over uw afspraak',
        icon: 'SG-afspraak-maken',
        url: '/alles-over-uw-afspraak',
        extraLabel: 'Met een doorverwijzing',
      },
      {
        label: 'Afdelingen en centra',
        icon: 'SG-afdelingen-en-centra',
        url: '/afdelingen-en-centra',
      },
      {
        label: 'Medische informatie',
        icon: 'SG-medische-informatie',
        url: '/folders',
      },
      { label: 'Vind uw arts', icon: 'SG-artsen', url: '/specialisten' },
      {
        label: 'MijnSpaarneGasthuis',
        icon: 'SG-user-fill',
        url: 'https://www.mijnspaarnegasthuis.nl/MyChart_PRD/Authentication/Login?',
      },
      {
        label: 'Bekijk onze wachttijden',
        icon: 'SG-wachttijden',
        url: '/wachttijden',
      },
      {
        label: 'Contact en locaties',
        icon: 'SG-contact',
        url: '/contact',
      },
    ],
    en: [
      {
        label: 'To the outpatient department',
        url: '/en/to-the-outpatient-department/at-the-outpatient-department',
      },
      {
        label: 'Hospital admission',
        url: '/en/hospital-admission',
      },
      {
        label: 'Medical information',
        url: '/en/folders',
      },
      {
        label: 'Emergency department',
        url: '/en/emergency-department',
      },
      {
        label: 'Patient information',
        url: '/en/patient-information',
      },
      {
        label: 'General visiting times',
        url: '/en/general-visiting-times',
      },
      {
        label: 'Contact a patient',
        url: '/en/contact-a-patient',
      },
      // {
      //   label: 'Contact us',
      //   url: '/en/contact-us',
      // },
      // {
      //   label: 'Holiday dialysis',
      //   url: '/en/holiday-dialysis',
      // },
    ],
  }

  return (
    <HeroPageHeader
      kind="home"
      title={props.title}
      intro={props.intro}
      visual={
        <div className="pageheader__visual fit-object">
          <div className="pageheader__wrapper">
            {/* {props.vimeoSrc.kind == 'loaded' && getCookie('video-cookies') === 'yes' && props.vimeoSrc.value && (
              <iframe
                className="pageheader__iframe"
                title="Spaarne Gasthuis"
                src={props.vimeoSrc.value}
                allow="autoplay"
                allowFullScreen
              />
            )} */}
            <Image
              src={pageheaderHeroSrc}
              srcset={pageheaderHeroSrcSet}
              sizes="100vw"
              lazyload={false}
              classNames={{ image: 'pageheader__image' }}
              alt="Patient met gebroken arm en naaste in gang"
              urls={Map<ImageSize, string>([
                ['640w', `/images/homepage-hero-640w.webp`],
                ['1280w', `/images/homepage-hero-1280w.webp`],
                ['1920w', `/images/homepage-hero-1920w.webp`],
                ['2880w', `/images/homepage-hero-2880w.webp`],
                ['1280w_original', `/images/homepage-hero-1280w_original.jpg`],
              ])}
              width="1280"
              height="480"
            />
          </div>
          <HomepageTopTasksLayout topTasks={i18next.language === 'nl' ? topTasks.nl : topTasks.en} />
        </div>
      }
      topTasks={
        <aside className="pageheader__top-tasks top-tasks">
          <div className="top-tasks__inner">
            {i18next.language === 'nl' ? (
              <ul className="top-tasks__list list--clickable has-icon">
                {topTasks.nl.map((task) => (
                  <li className="top-tasks__item list__item" key={task.label}>
                    <Link
                      url={task.url}
                      className="top-tasks__link list__link"
                      additionalOnClick={(_) => {
                        DataLayer.topTasks(task.label)
                      }}
                    >
                      <span className="top-tasks__subtitle">{task.label}</span>
                      <div className="top-tasks__icon list__icon">
                        <svg className="top-tasks__icon--svg" aria-hidden="true" width="26px" height="26px">
                          <use xlinkHref={`#icon--${task.icon}`}></use>
                        </svg>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="top-tasks__list list--clickable">
                {topTasks.en.map((task) => (
                  <li className="top-tasks__item list__item" key={task.label}>
                    <Link
                      url={task.url}
                      className="top-tasks__link list__link"
                      additionalOnClick={(_) => {
                        DataLayer.topTasks(task.label)
                      }}
                    >
                      <span className="top-tasks__subtitle">{task.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      }
    />
  )
}

export type UspItem = {
  text: string
  link: string
}

export const HomePageUSPs = () => {
  const usps: UspItem[] = [1, 2, 3, 4]
    .map((num) => {
      const text = i18next.t(`usps:usp${num}`, { defaultValue: '' })
      const link = i18next.t(`usps:usp${num}Link`, { defaultValue: '' })
      if (text) {
        return { text, link: link ?? '' }
      }
      return null
    }).filter(Boolean) as UspItem[]

  return (
    <ul className="usps">
      {usps.map(
        (usp, index) => {
          const uspItem = <p className="usps--intro">{usp.text}</p>
          return <li key={usp.text + index} className="usps__container">
            <svg className="usps--icon" aria-hidden="true" width="26px" height="26px">
              <use xlinkHref={`#icon--usps`}></use>
            </svg>
            {usp.link ? (
              <Link url={usp.link} className="usps__link link--inverted-gray">
                {uspItem}
              </Link>
            ) : <div>{uspItem}</div>}
          </li>
        }
      )}
    </ul>
  )
}
